import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { env } from '../../environments/environment';
import { ListFilters, ListResults, Setting } from '../shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class SettingService {
  constructor(protected http: HttpClient) {}

  _scoresEnabled;
  _flagsOnMarkupEnabled;

  cachedSettings: { [key: string]: any } = {};

  public list(filters?: ListFilters) {
    return this.http.get<ListResults<Setting>>(`${env.APIUrl}/setting`, { params: filters as any });
  }

  public get(id: number) {
    return this.http.get<Setting>(`${env.APIUrl}/setting/${id}`).toPromise();
  }

  public remove(id: number) {
    return this.http.delete<Setting>(`${env.APIUrl}/setting/${id}`).toPromise();
  }

  public edit(id: number, setting: Setting) {
    return this.http.put<Setting>(`${env.APIUrl}/setting/${id}`, setting).toPromise();
  }

  public create(setting: Setting) {
    return this.http.post<Setting>(`${env.APIUrl}/setting`, setting).toPromise();
  }

  public mobileUrls() {
    return this.http.get<{ android: string; ios: string }>(`${env.APIUrl}/setting/mobileUrls`).toPromise();
  }

  public ratingNotes() {
    return this.http.get<{ ratingNotes: { [key: string]: string } }>(`${env.APIUrl}/setting/ratingNotes`).toPromise();
  }

  public autoUnassignThreshold() {
    return this.http.get<number>(`${env.APIUrl}/setting/autoUnassignThreshold`).toPromise();
  }

  public assignedTimeFrame() {
    if (!this.cachedSettings['assignedTimeFrame']) {
      this.cachedSettings['assignedTimeFrame'] = this.http
        .get<number>(`${env.APIUrl}/setting/assignedTimeFrame`)
        .toPromise();
    }
    return this.cachedSettings['assignedTimeFrame'];
  }

  public needsReviewHideTimeframe() {
    if (!this.cachedSettings['needsReviewHideTimeframe']) {
      this.cachedSettings['needsReviewHideTimeframe'] = this.http
        .get<number>(`${env.APIUrl}/setting/needsReviewHideTimeframe`)
        .toPromise();
    }
    return this.cachedSettings['needsReviewHideTimeframe'];
  }

  public needsReviewHighlightTimeFrame() {
    if (!this.cachedSettings['needsReviewHighlightTimeFrame']) {
      this.cachedSettings['needsReviewHighlightTimeFrame'] = this.http
        .get<number>(`${env.APIUrl}/setting/needsReviewHighlightTimeFrame`)
        .toPromise();
    }
    return this.cachedSettings['needsReviewHighlightTimeFrame'];
  }

  public scoreThresholdAmber() {
    return this.findByKey('auto_flag_score_highlight_threshold_amber').then(setting => +setting.value);
  }

  public scoreThresholdOrange() {
    return this.findByKey('auto_flag_score_highlight_threshold_orange').then(setting => +setting.value);
  }

  public scoreThresholdRed() {
    return this.findByKey('auto_flag_score_highlight_threshold_red').then(setting => +setting.value);
  }

  // This setting won't change at runtime, so it's safe to cache
  public async scoresEnabled() {
    if (this._scoresEnabled === undefined) {
      this._scoresEnabled = await this.findByKey('enable_flag_scoring').then(
        setting => setting?.value.toLowerCase() === 'true',
      );
    }
    return this._scoresEnabled;
  }

  public async forceMenuEnabled() {
    const setting = await this.findByKey('enable_force_menu');
    return setting?.value.toLowerCase() === 'true';
  }

  public async flagsOnMarkupEnabled() {
    const setting = await this.findByKey('create_flag_on_markup_addition');
    return setting?.value.toLowerCase() === 'true';
  }

  public async galleryView() {
    const setting = await this.findByKey('show_condensed_gallery');
    return setting?.value.toLowerCase() === 'true' ? 'condensed' : 'grid';
  }

  public async sendRemindersDirectlyToInspectors() {
    const setting = await this.findByKey('send_reminders_directly_to_inspectors');
    return setting?.value.toLowerCase() === 'true';
  }

  public findByKey(key: string) {
    if (!this.cachedSettings[key]) {
      this.cachedSettings[key] = this.http.get<Setting>(`${env.APIUrl}/setting/key`, { params: { key } }).toPromise();
    }
    return this.cachedSettings[key];
  }

  public async signInWithGoogleEnabled(): Promise<boolean> {
    const res = await this.http.get<{ enabled: boolean }>(`${env.APIUrl}/setting/signInWithGoogle`).toPromise();
    return res?.enabled === true;
  }
}
